import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FabricObject } from 'fabric';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { useGrouping } from '../../grouping/hooks/use-grouping';
import { usePieceDispatch, usePieceSelector, usePieceStore } from '../../model/piece-context';
import { useModelComposition } from '../../model/use-model-composition';
import { getObjectsByLayerId } from '../../model/layer-id';
import { duplicateLayer } from '../../model/duplicate-layer';
import { useScrollSync } from '../hooks/use-scroll-sync';
import { useTrackWidth } from '../hooks/use-track-width';
import { usePlayback, playheadStore } from '../../playback';
import { useMedia } from '../../media';
import { getClipsInTrack } from '../timeline-selectors';
import { colors } from '../../theme';
import { isLayerObject } from '../../canvas/utils/canvas-pieces';
import { buildTextVideoMap, TimelineClipEntry } from '../utils/attachment-graph';
import {
  filterGroupableEntries,
  findGroupJumpTarget,
  LaneRegistry,
  resolveLaneKey,
  seedLaneRegistry
} from '../utils/timeline-rows';
import { TimelineToolbar } from './toolbar/timeline-toolbar';
import { LayerColumn, LayerEntry } from './layers/layer-column';
import { TrackArea } from './tracks/track-area';
import { dimensions } from '../../theme';
import { onModelRendered } from '../../canvas/canvas-events';

const MIN_DURATION = 1000;

type Entry = TimelineClipEntry;

export const TimelinePanel: React.FC = () => {
  const {
    canvas,
    selectedObject,
    transaction,
    bringForward,
    sendBackward,
    moveNextTo,
    canBringForward,
    canSendBackward
  } = useEditorCanvas();
  const { isMediaLoading } = useMedia();
  const modelComposition = useModelComposition();
  const dispatch = usePieceDispatch();
  const store = usePieceStore();
  const { ungroupSelection } = useGrouping(canvas, transaction);
  const [canvasObjectsVersion, setCanvasObjectsVersion] = useState(0);

  useEffect(() => {
    if (!canvas) return;
    return onModelRendered(canvas, () => setCanvasObjectsVersion(version => version + 1));
  }, [canvas]);

  const objectsByLayerId = useMemo(
    () => (canvas ? getObjectsByLayerId(canvas) : new Map<string, FabricObject>()),
    [canvas, modelComposition, canvasObjectsVersion]
  );

  const layerIdOf = (entry: LayerEntry): string | undefined =>
    entry.track.id.startsWith('track_') ? entry.track.id.slice(6) : undefined;
  const {
    aRef: layerScrollRef,
    bRef: trackScrollRef,
    onAScroll: onLayerScroll,
    onBScroll: onTrackScroll
  } = useScrollSync();
  const trackOuterRef = useRef<HTMLDivElement>(null);
  const trackWidth = useTrackWidth(trackOuterRef);

  const { isPlaying, play, pause, volume, setVolume } = usePlayback();
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(0.3);

  const mode = expanded ? 'expanded' : 'compact';
  const panelHeight = dimensions.timeline[mode].panelHeight;

  const modelDuration = usePieceSelector(m => m.duration);
  const totalDuration = Math.max(modelDuration, MIN_DURATION);

  const handleRewind = useCallback(() => playheadStore.set(0), []);
  const handleForward = useCallback(() => playheadStore.set(totalDuration), [totalDuration]);

  const entries: Entry[] = useMemo(
    () =>
      modelComposition.trackOrder.map(trackId => {
        const track = modelComposition.tracksById[trackId];
        const clip = getClipsInTrack(modelComposition, trackId)[0];
        const fabricObject = clip?.kind === 'fabric' ? objectsByLayerId.get(clip.objectId) : undefined;
        return { track, clip, fabricObject };
      }),
    [modelComposition, objectsByLayerId]
  );

  // DEBUG temporal — sacar cuando termines de probar timeAttach/laneKey.
  useEffect(() => {
    console.log(
      '[timeline] clips:',
      entries.map(e => ({
        id: e.clip?.id,
        trackKind: e.track.kind,
        label: e.track.label,
        start: e.clip?.start,
        duration: e.clip?.duration,
        laneKey: e.clip?.laneKey,
        timeAttachVideoId: e.clip?.timeAttach?.videoId
      }))
    );
  }, [entries]);

  // Registro de lanes (clip id -> lane + orden de creación), compartido por LayerColumn y
  // TrackArea para que su agrupamiento de filas (grupo inicial de video/texto vs. tracks propios)
  // coincida siempre, fila a fila, ya que se muestran lado a lado con scroll sincronizado. La lane
  // en sí se lee de Clip.laneKey (persistido, ver model/types.ts) — este registro es solo una
  // caché de esa sesión más el orden de creación, que no se persiste (los tracks nuevos siempre se
  // ordenan al final según el momento real en que se crearon, no según trackOrder).
  const laneRegistryRef = useRef<LaneRegistry | null>(null);
  if (laneRegistryRef.current === null) {
    laneRegistryRef.current = seedLaneRegistry(entries);
  }
  const laneRegistry = laneRegistryRef.current;

  // Igual que en TrackArea: el attachment graph (asociación video<->subtítulo, usada para el
  // borrado/duplicado en cascada) solo se arma con los clips del grupo compartido de video/texto.
  // Un track solo (nuevo o duplicado) nunca debe quedar enganchado a un video/subtítulo existente
  // solo por coincidir en el tiempo.
  const groupableEntries = useMemo(
    () => filterGroupableEntries(entries, laneRegistry),
    [entries, laneRegistry]
  );
  const textVideoByClipId = useMemo(() => buildTextVideoMap(groupableEntries), [groupableEntries]);

  // For the group-aware reorder below: maps each Fabric object back to its entry so we can resolve
  // the lane of whatever neighbor a move lands next to.
  const entryByFabricObject = useMemo(() => {
    const map = new Map<FabricObject, Entry>();
    entries.forEach(entry => {
      if (entry.fabricObject) map.set(entry.fabricObject, entry);
    });
    return map;
  }, [entries]);

  const handleSelect = (entry: Entry | undefined) => {
    if (!canvas || !entry?.clip || entry.clip.kind !== 'fabric') return;
    const obj = entry.fabricObject ?? getObjectsByLayerId(canvas).get(entry.clip.objectId);
    if (!obj) return;
    canvas.setActiveObject(obj);
    canvas.fire('selection:created', { selected: [obj], selectedObjects: [obj] } as never);
    canvas.requestRenderAll();
  };

  const handleToggleVisibility = (entry: LayerEntry) => {
    const next = !entry.track.visible;
    const layerId = layerIdOf(entry);
    if (layerId) dispatch({ type: 'setLayerVisible', id: layerId, visible: next });
    if (entry.fabricObject && canvas) {
      entry.fabricObject.set({ layerVisible: next });
      canvas.requestRenderAll();
    }
  };

  const handleToggleLock = (entry: LayerEntry) => {
    const next = !entry.track.locked;
    const layerId = layerIdOf(entry);
    if (layerId) dispatch({ type: 'setLayerLock', id: layerId, locked: next });
    if (entry.fabricObject && canvas) {
      entry.fabricObject.set({ selectable: !next, evented: !next });
      canvas.requestRenderAll();
    }
  };

  // Jumps past the whole lane in one move when the immediate neighbor belongs to a group with more
  // than one member (e.g. all the subtitles of a scene) — see findGroupJumpTarget. Falls back to
  // the plain single-step move otherwise (including reordering a clip among its own group's
  // siblings), so this changes nothing for the cases that already worked correctly.
  const handleBringForward = (e: LayerEntry) => {
    if (!e.fabricObject || !canvas) return;
    const stack = canvas.getObjects().filter(isLayerObject);
    const jumpTarget = findGroupJumpTarget(stack, entryByFabricObject, e.fabricObject, 1, laneRegistry);
    if (jumpTarget) moveNextTo(e.fabricObject, jumpTarget);
    else bringForward(e.fabricObject);
  };

  const handleSendBackward = (e: LayerEntry) => {
    if (!e.fabricObject || !canvas) return;
    const stack = canvas.getObjects().filter(isLayerObject);
    const jumpTarget = findGroupJumpTarget(stack, entryByFabricObject, e.fabricObject, -1, laneRegistry);
    if (jumpTarget) moveNextTo(e.fabricObject, jumpTarget);
    else sendBackward(e.fabricObject);
  };

  const handleDelete = (entry: LayerEntry) => {
    const layerId = layerIdOf(entry);
    if (!layerId) return;
    // Remove straight from the model; the reconciler drops the Fabric object (stale-removal).
    // Selection isn't in the model, so clear a dangling active object here first (UI concern).
    if (canvas && entry.fabricObject && canvas.getActiveObject() === entry.fabricObject) {
      canvas.discardActiveObject();
    }

    // Borrar un video se lleva puestos los subtítulos que le pertenecen (misma escena).
    // clip.id === layerId en este modelo, así que el id del clip de texto es directamente el layerId a borrar.
    if (entry.track.kind === 'video' && entry.clip) {
      const videoClipId = entry.clip.id;
      textVideoByClipId.forEach((linkedVideoId, textClipId) => {
        if (linkedVideoId === videoClipId) dispatch({ type: 'removeLayer', id: textClipId });
      });
    }

    dispatch(
      entry.track.kind === 'audio' ? { type: 'removeAudioLayer', id: layerId } : { type: 'removeLayer', id: layerId }
    );
  };

  const handleDuplicate = (entry: LayerEntry) => {
    const layerId = layerIdOf(entry);
    if (!layerId) return;
    const model = store.getState();
    const src = model.layers[layerId];
    if (!src) return;

    // Sin importar el track (grupo inicial de video/texto, o un track propio agregado después),
    // el duplicado siempre se ubica al final de ese mismo track — nunca superpuesto al original
    // ni reubicado según lo que haya en otros tracks. duplicateLayer clona el laneKey del original
    // tal cual (structuredClone), así que la copia hereda su lane sin necesidad de registrarla aparte.
    const laneKey = resolveLaneKey(entry, laneRegistry);
    const laneEnd = entries.reduce((max, e) => {
      if (!e.clip || resolveLaneKey(e, laneRegistry) !== laneKey) return max;
      return Math.max(max, e.clip.start + e.clip.duration);
    }, 0);

    if (entry.track.kind === 'video') {
      // Duplicar un video se lleva puestos los subtítulos que le pertenecen (misma escena
      // inicial): todo el grupo se reubica junto, al final del mismo track.
      const linkedTextIds = Array.from(textVideoByClipId.entries())
        .filter(([, linkedVideoId]) => linkedVideoId === layerId)
        .map(([textClipId]) => textClipId);

      const delta = laneEnd - src.start;

      const videoIndex = model.layerOrder.indexOf(layerId);
      const videoCopy = duplicateLayer(src);
      videoCopy.start = laneEnd;
      dispatch({ type: 'addLayer', layer: videoCopy, index: videoIndex >= 0 ? videoIndex + 1 : undefined });

      linkedTextIds.forEach(textLayerId => {
        const textLayer = model.layers[textLayerId];
        if (!textLayer) return;
        const textIndex = model.layerOrder.indexOf(textLayerId);
        const textCopy = duplicateLayer(textLayer);
        textCopy.start = textLayer.start + delta;
        // duplicateLayer clones timeAttach as-is (still pointing at the original video) — repoint
        // it at the duplicated video so the copy stays linked to its own scene, not the original's.
        if (textCopy.kind !== 'audio') textCopy.timeAttach = { videoId: videoCopy.id };
        dispatch({ type: 'addLayer', layer: textCopy, index: textIndex >= 0 ? textIndex + 1 : undefined });
      });
      return;
    }

    const copy = duplicateLayer(src);
    copy.start = laneEnd;
    if (copy.kind === 'audio') {
      dispatch({ type: 'addAudioLayer', layer: copy });
      return;
    }
    // Sit the copy right above the original in the stack.
    const index = model.layerOrder.indexOf(layerId);
    dispatch({ type: 'addLayer', layer: copy, index: index >= 0 ? index + 1 : undefined });
  };

  const canMoveUp = (e: LayerEntry) => !!e.fabricObject && canBringForward(e.fabricObject);
  const canMoveDown = (e: LayerEntry) => !!e.fabricObject && canSendBackward(e.fabricObject);

  return (
    <div
      style={{
        height: panelHeight,
        minHeight: panelHeight,
        backgroundColor: colors.panelBg,
        borderTop: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'height 0.2s ease, min-height 0.2s ease'
      }}
    >
      <TimelineToolbar
        duration={modelDuration}
        isPlaying={isPlaying}
        playDisabled={isMediaLoading}
        volume={volume}
        zoom={zoom}
        expanded={expanded}
        onPlay={play}
        onPause={pause}
        onRewind={handleRewind}
        onForward={handleForward}
        onVolumeChange={setVolume}
        onZoomChange={setZoom}
        onToggleExpanded={() => setExpanded(v => !v)}
      />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <LayerColumn
          entries={entries}
          laneRegistry={laneRegistry}
          selectedObject={selectedObject}
          mode={mode}
          scrollRef={layerScrollRef}
          onScroll={onLayerScroll}
          onSelectLayer={handleSelect}
          onToggleVisibility={handleToggleVisibility}
          onToggleLock={handleToggleLock}
          onUngroup={ungroupSelection}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          onBringForward={handleBringForward}
          onSendBackward={handleSendBackward}
        />
        <TrackArea
          entries={entries}
          laneRegistry={laneRegistry}
          selectedObject={selectedObject}
          totalDuration={totalDuration}
          trackWidth={trackWidth}
          mode={mode}
          outerRef={trackOuterRef}
          scrollRef={trackScrollRef}
          onScroll={onTrackScroll}
          onSelectClip={handleSelect}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
};
